<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { useSettingsStore } from '@/stores/SettingsStore.ts';
import { nextTick, onMounted, onUnmounted, reactive, watch } from 'vue';
import { unregisterAllHotkeysAction } from '@/actions/electron/UnregisterAllHotkeysAction.ts';
import { BlockedHotkeyCombinations, DefaultHotkeys } from '@/constants/DefaultHotkeys.ts';
import { isAccelerator } from '@/utils/HotkeyUtils.ts';
import { createTextToast } from '@/utils/ToastUtils.ts';
import { Label } from '@/components/ui/label';
import { registerHotkeysAction } from '@/actions/electron/RegisterHotkeysAction.ts';
import { Input } from '@/components/ui/input';
import { logger } from '@/lib/logger.ts';
import { eventEmitter } from '@/lib/eventEmitter.ts';
import { HOTKEY_LABELS } from '@/constants/HotkeyLabels.ts';

const settingsStore = useSettingsStore();

interface ComponentState {
    hotkeysCopy: Record<string, string>;
    selectedHotkey: string | number | null;
    currentSelection: string;
    currentSelectionValid: boolean;
    changesMade: boolean;
}

const state: ComponentState = reactive({
    hotkeysCopy: {},
    selectedHotkey: null,
    currentSelection: '',
    currentSelectionValid: false,
    changesMade: false,
});

const save = () => {
    settingsStore.hotkeys = JSON.parse(JSON.stringify(state.hotkeysCopy));
    state.changesMade = false;
    eventEmitter.emit('setSettingsDialogVisibility', {
        visible: false,
    });
};

const selectHotkey = (hotkey: string | number | null) => {
    if (hotkey !== null && state.selectedHotkey !== null) {
        return;
    }

    state.selectedHotkey = hotkey;
};

const resetToDefaults = () => {
    state.selectedHotkey = null;
    state.hotkeysCopy = JSON.parse(JSON.stringify(DefaultHotkeys));
};

const handleKeydown = (e: KeyboardEvent) => {
    let modifier = null;

    if (e.key === 'Escape') {
        state.selectedHotkey = null;

        return;
    }

    if (e.ctrlKey && e.altKey) {
        modifier = 'AltGr';
    } else if (e.shiftKey) {
        modifier = 'Shift';
    } else if (e.altKey) {
        modifier = 'Alt';
    } else if (e.ctrlKey) {
        modifier = 'CmdOrCtrl';
    } else if (e.metaKey) {
        modifier = 'CmdOrCtrl';
    }

    // Make modifier always be the short version
    switch (modifier) {
        case 'Command':
            modifier = 'Cmd';
            break;
        case 'Control':
            modifier = 'Ctrl';
            break;
        case 'CommandOrControl':
            modifier = 'CmdOrCtrl';
            break;
    }

    let key = e.key;

    if (!modifier || !key) {
        logger.debug('Invalid input:', e);

        return;
    }

    if (key.includes('Numpad')) {
        key = e.code;
        key = key.replace('Numpad', 'num');
        key = key.toLowerCase();
    }

    if (key.includes('Key')) {
        key = key.toUpperCase();
    }

    switch (key) {
        case 'numdecimal':
            key = 'numdec';
            break;
        case 'numsubtract':
            key = 'numsub';
            break;
        case 'nummultiply':
            key = 'nummult';
            break;
        case 'numdivide':
            key = 'numdiv';
            break;
        case 'NumLock':
            key = 'Numlock';
            break;
    }

    if (key.startsWith('Arrow')) {
        key = key.replace('Arrow', '');
    }

    const accelerator = `${modifier}+${key}`;
    state.currentSelection = accelerator;

    const isValid = isAccelerator(accelerator);
    state.currentSelectionValid = isValid;

    logger.debug({
        accelerator: accelerator,
        isValid: isValid,
    });

    // Check if blocked accelerator
    if (BlockedHotkeyCombinations.includes(accelerator)) {
        createTextToast(
            'This key combination is not allowed due to system limitations. Please try a different combination.',
            'Please try a different combination.',
            'destructive',
            false,
        );

        return;
    }

    // Check if already in use
    for (const [shortcut, existingAccelerator] of Object.entries(state.hotkeysCopy)) {
        if (shortcut !== state.selectedHotkey && accelerator === existingAccelerator) {
            createTextToast(
                'This key combination is already in use.',
                'Please try a different combination.',
                'destructive',
                false,
            );

            return;
        }
    }

    if (isValid && state.selectedHotkey !== null) {
        state.hotkeysCopy = {
            ...state.hotkeysCopy,
            [state.selectedHotkey]: accelerator,
        };
        state.selectedHotkey = null;
    }
};

onMounted(async () => {
    unregisterAllHotkeysAction();

    window.addEventListener('keydown', handleKeydown);

    state.hotkeysCopy = JSON.parse(JSON.stringify(settingsStore.hotkeys));
    await nextTick();
    state.changesMade = false;
});

onUnmounted(() => {
    registerHotkeysAction(JSON.parse(JSON.stringify(settingsStore.hotkeys)));

    window.removeEventListener('keydown', handleKeydown);
});

watch(
    () => [state.hotkeysCopy],
    async () => {
        state.changesMade = true;
    },
    { deep: true },
);
</script>

<template>
    <main class="flex-shrink flex-grow overflow-y-auto p-4 pt-1">
        <p class="mb-4 text-sm">
            A valid hotkey is a combination of a modifier and a key. See
            <a
                href="https://www.electronjs.org/docs/latest/api/accelerator#available-modifiers"
                target="_blank"
                class="underline"
                >the following page</a
            >
            for all available combinations. Single keys can not be used as hotkeys due to system
            limitations.
        </p>

        <div v-if="state.selectedHotkey !== null" class="mb-4 text-sm font-bold text-yellow-500">
            Setting hotkey for action: "{{ (HOTKEY_LABELS as any)[state.selectedHotkey] }}", waiting
            for valid input...
        </div>

        <div class="hotkey-input-wrapper mb-4">
            <div v-for="(accelerator, action) in state.hotkeysCopy" :key="action">
                <Label>{{ (HOTKEY_LABELS as any)[action] }}</Label>
                <Input
                    type="text"
                    :placeholder="
                        state.selectedHotkey === action ? 'Waiting for valid input...' : accelerator
                    "
                    class="mt-2 focus-visible:ring-0"
                    :class="{ 'border-white': state.selectedHotkey === action }"
                    readonly
                    @click="selectHotkey(action)"
                />
            </div>
        </div>
    </main>

    <footer class="flex justify-end border-t p-4">
        <div class="flex w-full justify-between">
            <div>
                <Button
                    type="button"
                    :disabled="state.selectedHotkey === null"
                    @click="selectHotkey(null)"
                    variant="destructive"
                    class="me-2"
                >
                    Cancel
                </Button>
                <Button
                    type="button"
                    variant="secondary"
                    @click="resetToDefaults"
                    class="mt-2 md:mt-0"
                >
                    Reset to defaults
                </Button>
            </div>
            <Button type="button" @click="save()" variant="success" :disabled="!state.changesMade">
                Save and close
            </Button>
        </div>
    </footer>
</template>

<style scoped>
.hotkey-input-wrapper {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(4, 1fr);
    grid-column-gap: 1rem;
    grid-row-gap: 1rem;
}
</style>
